import { PublicSiteApi, WebApiCustomListsDtoCreateCustomListItemDto, WebApiCustomListsDtoCustomListItemDto, WebApiCustomListsDtoUpdateCustomListItemDto, WebApiMemberDtoAddMemberFromPublicSiteDto, WebApiMemberDtoCreatePasswordCodeDto, WebApiModelsCustomListField, WebApiPublicSiteDtoAddOwnFamilyMemberDto, WebApiPublicSiteDtoEditableCustomListDto, WebApiPublicSiteDtoLoginWithPasswordCodeDto, WebApiPublicSiteDtoUpdateFamilyMemberSelfDto, WebApiPublicSiteDtoUpdateOwnFamilyMemberDto, WebApiServicesDataTransferObjectsGroupServiceActiveUserByPasswordCode, WebApiServicesDataTransferObjectsGroupServiceConfirmMemberEmailChangeDto, WebApiServicesDataTransferObjectsGroupServiceUpdatePublicMemberDto, } from "../Repository/eventbookyapi";
import { GetApiConfigurationMember } from "./ServiceConfiguration";

export type saveMemberEventReplyRequest = {
    eventUniqueId: string,
    answer: string,
    timeAnswers: { timeId: number, customerAnswer: number }[],
    /** The people of the household to sign up, each with own answers. Left out when the member only answers for themselves. */
    people?: householdPersonAnswer[]
}

export type householdPersonAnswer = {
    memberId: number,
    timeAnswers: { timeId: number, customerAnswer: number }[]
}

/** A person a primary member can sign up for an event, with the reply the person already has. */
export type householdMember = {
    memberId: number,
    name: string,
    /** The primary member, who signs up the household. */
    isSelf: boolean,
    replyId: number,
    replyTime: { timeId: number, customerAnswerType: number }[]
}

export type memberEventTime = {
    start: string,
    end: string,
    isFullDay: boolean,
    answer?: number | null,
    isFullyBooked: boolean,
    isWaitlistOpen: boolean
}

export type memberEvent = {
    uniqueId: string,
    name: string,
    description?: string,
    location?: string,
    deadline?: string,
    hasReplied: boolean,
    /** 0 is sign-up, 1 is a poll about the best time. */
    eventType: number,
    answerOptions: number,
    times: memberEventTime[]
}

export abstract class PublicSiteService {

    // The coming events the company shows on the member site, with the logged in member's own answers.
    public static GetMemberEvents = async (): Promise<memberEvent[]> => {
        const response = await fetch(process.env.REACT_APP_API_URL + '/PublicSite/GetMemberEvents', {
            credentials: 'include'
        });

        if (!response.ok) {
            throw Object.assign(new Error("Could not load the events"), { status: response.status });
        }

        return await response.json();
    };

    // The receipt (PDF) of the Ping Payments link the member paid the year with, fetched from Ping on every call.
    public static GetMemberPaymentReceipt = async (year: number): Promise<Blob> => {
        const response = await fetch(process.env.REACT_APP_API_URL + '/PublicSite/GetMemberPaymentReceipt?year=' + year, {
            credentials: 'include'
        });

        if (!response.ok) {
            throw Object.assign(new Error("Could not load the receipt"), { status: response.status });
        }

        return await response.blob();
    };

    // Who the logged in member is and the reply the member already has for the event. Fails when not logged in.
    public static GetEventReply = async (eventUniqueId: string) => {
        const response = await fetch(process.env.REACT_APP_API_URL + '/PublicSite/GetEventReply?eventUniqueId=' + encodeURIComponent(eventUniqueId), {
            credentials: 'include'
        });

        if (!response.ok) {
            throw Object.assign(new Error("Not logged in as a member"), { status: response.status });
        }

        return await response.json();
    };

    // Creates or changes the logged in member's reply for the event.
    public static SaveEventReply = async (request: saveMemberEventReplyRequest) => {
        const response = await fetch(process.env.REACT_APP_API_URL + '/PublicSite/SaveEventReply', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(request)
        });

        if (!response.ok) {
            throw Object.assign(new Error("Could not save the reply"), { status: response.status });
        }

        return await response.json();
    };

    public static Logout = async () => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteLogoutPost();
    };

    public static CreatePasswordCode = async (request: WebApiMemberDtoCreatePasswordCodeDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteCreatePasswordCodePost({
            webApiMemberDtoCreatePasswordCodeDto: request
        });
    };

    public static LoginWithPasswordCode = async (request: WebApiPublicSiteDtoLoginWithPasswordCodeDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteLoginWithPasswordCodePost({
            webApiPublicSiteDtoLoginWithPasswordCodeDto: request
        });
    };

    public static GetMember = async () => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetMemberGet();
    };

    public static UpdateMember = async (request: WebApiServicesDataTransferObjectsGroupServiceUpdatePublicMemberDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteUpdateMemberPost({
            webApiServicesDataTransferObjectsGroupServiceUpdatePublicMemberDto: request
        });
    };

    // Which self-edit page to render for the logged-in member (regular vs. family).
    public static GetSession = async () => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetSessionGet();
    };

    // Membership fee status per year (+ pay links when enabled) for the logged-in member.
    public static GetMemberPayments = async () => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetMemberPaymentsGet();
    };

    public static GetFamilyMemberSelf = async () => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetFamilyMemberSelfGet();
    };

    public static UpdateFamilyMemberSelf = async (request: WebApiPublicSiteDtoUpdateFamilyMemberSelfDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteUpdateFamilyMemberSelfPost({
            webApiPublicSiteDtoUpdateFamilyMemberSelfDto: request
        });
    };

    public static GetMyFamilyMembers = async () => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetMyFamilyMembersGet();
    };

    public static AddMyFamilyMember = async (request: WebApiPublicSiteDtoAddOwnFamilyMemberDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteAddMyFamilyMemberPost({
            webApiPublicSiteDtoAddOwnFamilyMemberDto: request
        });
    };

    public static UpdateMyFamilyMember = async (request: WebApiPublicSiteDtoUpdateOwnFamilyMemberDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteUpdateMyFamilyMemberPost({
            webApiPublicSiteDtoUpdateOwnFamilyMemberDto: request
        });
    };

    public static DeleteMyFamilyMember = async (memberId: number) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteDeleteMyFamilyMemberPost({
            memberId: memberId
        });
    };

    public static AddmemberByMember = async (request: WebApiMemberDtoAddMemberFromPublicSiteDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteAddMemberFromPublicSitePost({
            webApiMemberDtoAddMemberFromPublicSiteDto: request
        });
    };

    public static GetMemberFieldsByUrl = async (url: string) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetMemberFieldsPublicSiteByUrlGet({ url });
    };

    public static GetMembershipProductsByUrl = async (url: string) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetMembershipProductsFromPublicSiteByUrlGet({ url });
    };


    public static ActiveMemberAccount = async (request: WebApiServicesDataTransferObjectsGroupServiceActiveUserByPasswordCode) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteActivateAccountFromPublicSitePost({
            webApiServicesDataTransferObjectsGroupServiceActiveUserByPasswordCode: request
        });
    };

    public static ConfirmEmailChange = async (request: WebApiServicesDataTransferObjectsGroupServiceConfirmMemberEmailChangeDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteConfirmEmailChangePost({
            webApiServicesDataTransferObjectsGroupServiceConfirmMemberEmailChangeDto: request
        });
    };

    public static GetCompanyPublicSiteByUrl = async (url: string) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetCompanyPublicSiteByUrlGet({ url: url });
    };

    public static HasPublicMemberView = async (url: string) => {
        const result = await new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteHasPublicMemberViewGet({ url });
        // API returns string "true"/"false" instead of boolean
        return result === true || (result as any) === 'true';
    };

    public static GetPublicMemberView = async (url: string) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteGetPublicMemberViewGet({ url });
    };

    public static GetEditableCustomLists = async () => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteEditableCustomListsGet();
    };

    public static GetEditableCustomListItems = async (customListId: number) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteCustomListItemsCustomListIdGet({ customListId });
    };

    // All items in a list, for lookup/dropdown fields that reference another list - only works
    // for lists flagged member-visible (MembersEditRights.OwnItems) server-side.
    public static GetCustomListItemsForLookup = async (customListId: number) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteCustomListItemsCustomListIdLookupGet({ customListId });
    };

    public static CreateEditableCustomListItem = async (customListId: number, dto: WebApiCustomListsDtoCreateCustomListItemDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteCustomListItemsCustomListIdPost({
            customListId,
            webApiCustomListsDtoCreateCustomListItemDto: dto
        });
    };

    public static UpdateEditableCustomListItem = async (customListId: number, id: number, dto: WebApiCustomListsDtoUpdateCustomListItemDto) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteCustomListItemsCustomListIdIdPost({
            customListId,
            id,
            webApiCustomListsDtoUpdateCustomListItemDto: dto
        });
    };

    public static DeleteEditableCustomListItem = async (customListId: number, id: number) => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteCustomListItemsCustomListIdIdDelete({
            customListId,
            id
        });
    };

    public static GetEditableCustomListFields = async (customListId: number): Promise<WebApiModelsCustomListField[]> => {
        return new PublicSiteApi(GetApiConfigurationMember()).apiPublicSiteEditableCustomListsCustomListIdFieldsGet({ customListId });
    };
};
